import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Autocomplete,
  Chip,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createQuestion } from '../services/questionService';
import Footer from '../components/Footer';

const AskQuestion = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);

  // Quill Modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
    ],
  };

  // Quill Formats configuration
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block',
    'list', 'bullet',
    'script',
    'indent',
    'link', 'image',
    'color', 'background',
    'font',
    'align',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!title.trim()) {
        throw new Error('Title is required');
      }

      if (!content.trim()) {
        throw new Error('Content is required');
      }

      const formattedTags = tags.slice(0, 5).map(tag => tag.trim()).filter(tag => tag);
      
      const questionData = {
        title: title.trim(),
        content: content.trim(),
        tags: formattedTags,
        user_id: 1, // Default user ID for testing
        created_at: new Date().toISOString(),
      };

      console.log('Submitting question data:', questionData);

      const response = await createQuestion(questionData);
      
      if (response && response.id) {
        navigate(`/questions/${response.id}`);
      } else {
        throw new Error('Failed to create question');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to create question: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        py: 2, 
        backgroundColor: '#000000',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <img 
          src={require('../assets/logo0.png')} 
          alt="Logo" 
          style={{ 
            height: '30px',
            filter: 'brightness(0) invert(1)' // This will make the logo white
          }} 
        />
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Link to="/all-questions" style={{ textDecoration: 'none' }}>
            <Button color="primary">All Questions</Button>
          </Link>
          <Link to="/ai-detect" style={{ textDecoration: 'none' }}>
            <Button color="primary">AI Detect</Button>
          </Link>
          <Link to="/fast-scan" style={{ textDecoration: 'none' }}>
            <Button color="primary">Fast Scan</Button>
          </Link>
          <Link to="/under" style={{ textDecoration: 'none' }}>
            <Button color="primary">Under</Button>
          </Link>
        </Box>

        <Typography variant="h4" gutterBottom>
          Ask a Question
        </Typography>

        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              sx={{ mb: 3 }}
              helperText="Be specific and imagine you're asking a question to another person"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <div className="border rounded-md">
                <ReactQuill
                  value={content}
                  onChange={setContent}
                  modules={modules}
                  formats={formats}
                  placeholder="Provide all the details someone would need to answer your question..."
                  className="min-h-[200px]"
                  theme="snow"
                />
              </div>
            </div>

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={tags}
              onChange={(_, newValue) => setTags(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  helperText="Add up to 5 tags to describe what your question is about"
                />
              )}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              size="large"
            >
              {loading ? 'Posting...' : 'Post Your Question'}
            </Button>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </>
  );
};

export default AskQuestion; 